import { StyledIconBase } from "@styled-icons/styled-icon";
import { backgrounds } from "assets/backgrounds";
import {
  Add,
  ArrowPointDown,
  ArrowPointUp,
  Copy,
  Delete,
  WidgetMoreOptions,
} from "assets/icons/Common";
import { getRandomBackground } from "components/multipleDashboard/utils";
import { Col, HorizontalLine, IconButton, Row } from "components/primitives";
import { useConfirm } from "contexts/ConfirmContext";
import { DashboardContext, INITIAL_REMEMBER_SETTINGS } from "contexts/DashboardContext";
import { useWidgets } from "hooks/widgets";
import { FC, useCallback, useContext, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import styled from "styled-components";
import { DashboardProps, GlobalData } from "types/GlobalData";
import {
  findDashboardGroupById,
  getValidDashboards,
  isValidDashboardGroup,
  reorderDashboardsById,
} from "util/dashboardGroups";
import { v4 as uuidv4 } from "uuid";

import { DashboardItem } from "./DashboardItem";

interface Props {
  groupId: string;
  groupIndex: number;
}

// Writes rebuild only the target group so unrelated groups keep their identity,
// and the target group's dashboards are sanitized so invalid persisted entries are
// dropped instead of being written back.
const replaceGroupDashboards = (
  groups: GlobalData["dashboardGroups"],
  groupId: string,
  dashboards: unknown,
): GlobalData["dashboardGroups"] =>
  (groups || []).map((group) =>
    group && group.id === groupId
      ? { ...group, dashboards: getValidDashboards(dashboards) }
      : group,
  );

export const GroupItem: FC<Props> = ({ groupId, groupIndex }) => {
  const {
    data: globalData,
    setData: updateGlobalData,
    wpLoadData,
    wpSaveData,
  } = useContext(DashboardContext);
  const confirm = useConfirm();
  const { widgets, duplicateWidget, removeWidget } = useWidgets(wpLoadData, wpSaveData);
  const dashboardGroups = (globalData.dashboardGroups || []).filter(isValidDashboardGroup);
  const selectedGroup = dashboardGroups.find((group) => group.id === groupId);
  const [showGroupOptions, setShowGroupOptions] = useState(false);
  const [nameValue, setNameValue] = useState(selectedGroup?.name);
  const nameInput = useRef<HTMLInputElement>(null);
  const namePara = useRef<HTMLInputElement>(null);
  const noBlurRef = useRef<HTMLInputElement>(null);

  const dashboards = getValidDashboards(selectedGroup?.dashboards);

  const handleUpdateOrder = useCallback(
    (draggedId: string, targetId: string) => {
      const foundGroup = findDashboardGroupById(globalData?.dashboardGroups, groupId);
      if (!foundGroup) {
        return;
      }

      const { dashboards: reordered, changed } = reorderDashboardsById(
        foundGroup.dashboards,
        draggedId,
        targetId,
      );

      // A no-op drop should not write state unless invalid entries need cleaning.
      if (!changed) {
        return;
      }

      updateGlobalData({
        dashboardGroups: replaceGroupDashboards(globalData.dashboardGroups, groupId, reordered),
      });
    },
    [globalData, updateGlobalData, groupId],
  );

  if (!selectedGroup) {
    return null;
  }

  const handleDashboardNameChange = (id: string, name: string) => {
    const newDashboard = dashboards.find((dashboard) => dashboard.id === id);
    if (newDashboard) {
      const newData = { ...newDashboard, name };
      updateGlobalData({
        dashboardGroups: replaceGroupDashboards(
          globalData.dashboardGroups,
          groupId,
          dashboards.map((dashboard) => (dashboard.id === id ? newData : dashboard)),
        ),
      });
    }
  };

  const handleCreateNewSlide = (groupID: string) => {
    const newDashboardId = uuidv4();
    const newDashboard: DashboardProps = {
      id: newDashboardId,
      name: "New Slide",
      backgroundName: getRandomBackground(dashboards),
      fontName: "LEMON MILK Pro, Arial, sans-serif",
      rememberSettings: INITIAL_REMEMBER_SETTINGS,
      voiceType: "Female",
    };

    const newDashboardGroups = [...globalData.dashboardGroups];
    const foundGroup = findDashboardGroupById(newDashboardGroups, groupID);
    if (foundGroup) {
      updateGlobalData({
        dashboardGroups: replaceGroupDashboards(newDashboardGroups, groupID, [
          ...dashboards,
          newDashboard,
        ]),
      });
    }
  };

  const handleDuplicateDashboard = (id: string) => {
    const newDashboard = dashboards.find((dashboard) => dashboard.id === id);
    if (newDashboard) {
      const newDashboardId = uuidv4();
      const newData = { ...newDashboard, name: `${newDashboard.name} (Copy)`, id: newDashboardId };

      Object.keys(widgets).forEach((widgetId) => {
        if (widgetId.includes(id)) {
          const newWidgetId = duplicateWidget(widgetId, newDashboardId);
        }
      });

      updateGlobalData({
        dashboardGroups: replaceGroupDashboards(globalData.dashboardGroups, groupId, [
          ...dashboards,
          newData,
        ]),
      });
    }
  };

  const removeWidgetsByDashboard = (id: string) => {
    Object.keys(widgets).map((widgetId) => {
      if (widgetId.includes(id)) {
        removeWidget(widgetId);
      }
    });
  };

  const handleDeleteDashboard = async (id: string) => {
    const confirmed = await confirm({
      title: "Info",
      message: "Are you sure you want to delete this dashboard?",
      confirmText: "Ok",
      cancelText: "Cancel",
    });
    console.log("result===>", confirmed);
    if (!confirmed) {
      return;
    }

    // if(!window.confirm("Are you sure you want to delete this dashboard?")) {
    //   return;
    // }
    removeWidgetsByDashboard(id);
    const newDashboards = dashboards.filter((dashboard) => dashboard.id !== id);
    const activeDashboardId =
      globalData.activeDashboardId === id
        ? (newDashboards[0]?.id ?? "")
        : globalData.activeDashboardId;
    if (newDashboards) {
      updateGlobalData({
        dashboardGroups: replaceGroupDashboards(globalData.dashboardGroups, groupId, newDashboards),
        activeDashboardId,
      });
    }
  };

  const handleRenameGroupClick = () => {
    namePara.current?.click();
    setShowGroupOptions(false);
  };

  const handleDeleteGroupClick = async () => {
    setShowGroupOptions(false);

    const confirmed = await confirm({
      title: "Info",
      message: "Are you sure you want to delete this group?",
      confirmText: "Ok",
      cancelText: "Cancel",
    });

    if (!confirmed) {
      return;
    }
    // if(!window.confirm("Are you sure you want to delete this group?")) {
    //   return;
    // }

    dashboards.forEach((dashboard) => {
      removeWidgetsByDashboard(dashboard.id);
    });
    const newDashboardGroups = dashboardGroups.filter((group) => group.id !== groupId);
    const activeDashboardGroupId =
      globalData.activeDashboardGroupId === groupId
        ? (newDashboardGroups[0]?.id ?? "")
        : globalData.activeDashboardGroupId;
    const activeDashboardId =
      globalData.activeDashboardGroupId === groupId
        ? (getValidDashboards(newDashboardGroups[0]?.dashboards)[0]?.id ??
          globalData.activeDashboardId)
        : globalData.activeDashboardId;
    updateGlobalData({
      dashboardGroups: newDashboardGroups,
      activeDashboardGroupId,
      activeDashboardId,
    });
  };

  const handleBlur: React.FocusEventHandler = (event) => {
    // Check if the next focused element (relatedTarget) is inside the noBlur element
    if (noBlurRef.current && !noBlurRef.current.contains(event.relatedTarget)) {
      setShowGroupOptions(false);
    }
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNameValue(e.target.value);
  };

  const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (namePara.current) {
      namePara.current.style.display = "block";
    }
    const newGroup = { ...selectedGroup, name: e.target.value };
    updateGlobalData({
      dashboardGroups: dashboardGroups.map((group) => {
        if (group.id === groupId) {
          return newGroup;
        }
        return group;
      }),
    });
    (e.target as HTMLElement).style.display = "none";
  };

  const handleNameClick: React.MouseEventHandler = (e) => {
    if (nameInput.current) {
      nameInput.current.style.display = "block";
      nameInput.current.focus();
      (e.target as HTMLElement).style.display = "none";
    }
  };

  const handleGroupMoveUp = () => {
    if (groupIndex > 0) {
      const newDashboardGroups = [...dashboardGroups];
      newDashboardGroups[groupIndex - 1] = selectedGroup;
      newDashboardGroups[groupIndex] = dashboardGroups[groupIndex - 1];
      updateGlobalData({
        dashboardGroups: newDashboardGroups,
      });
    }
  };

  const handleGroupMoveDown = () => {
    if (groupIndex < dashboardGroups.length - 1) {
      const newDashboardGroups = [...dashboardGroups];
      newDashboardGroups[groupIndex + 1] = selectedGroup;
      newDashboardGroups[groupIndex] = dashboardGroups[groupIndex + 1];
      updateGlobalData({
        dashboardGroups: newDashboardGroups,
      });
    }
  };

  return (
    <OuterContainer>
      <HeaderRow>
        <GroupName>
          <p ref={namePara} onClick={(e) => handleNameClick(e)}>
            {selectedGroup.name || "New Group"}
          </p>
          <NameInput
            ref={nameInput}
            type="text"
            value={nameValue}
            onBlur={handleGroupNameChange}
            onChange={handleValueChange}
            maxLength={30}
          />
        </GroupName>
        <ActionButton Icon={ArrowPointUp} onClick={handleGroupMoveUp} disabled={groupIndex === 0} />
        <ActionButton
          Icon={ArrowPointDown}
          onClick={handleGroupMoveDown}
          disabled={groupIndex === dashboardGroups.length - 1}
        />
        <ActionButton
          Icon={WidgetMoreOptions}
          onClick={() => setShowGroupOptions((prev) => !prev)}
          onBlur={handleBlur}
        />
        {showGroupOptions && (
          <GroupOptions ref={noBlurRef}>
            <TextBtn onClick={handleRenameGroupClick}>
              <Copy />
              Rename
            </TextBtn>
            <HorizontalLine style={{ margin: 0 }} />
            <TextBtn
              color="var(--danger)"
              onClick={handleDeleteGroupClick}
              disabled={dashboardGroups.length === 1}
            >
              <Delete />
              Delete
            </TextBtn>
          </GroupOptions>
        )}
      </HeaderRow>
      <DndProvider backend={HTML5Backend} context={window}>
        <DashboardRow>
          {dashboards.map((dashboard) => {
            return (
              <DashboardItem
                key={dashboard.id}
                groupId={groupId}
                backgroundUrl={backgrounds["Purple"]}
                dashboard={dashboard}
                handleDashboardNameChange={handleDashboardNameChange}
                handleDuplicateDashboard={handleDuplicateDashboard}
                handleDeleteDashboard={handleDeleteDashboard}
                onUpdateOrder={handleUpdateOrder}
              />
            );
          })}
          <ItemWrap>
            <CreateNewSlide
              onClick={() => handleCreateNewSlide(groupId)}
              disabled={dashboards.length === 10}
            >
              <Add />
              Create new slide
            </CreateNewSlide>
          </ItemWrap>
        </DashboardRow>
      </DndProvider>
    </OuterContainer>
  );
};

const OuterContainer = styled.div`
  padding: 10px;
  background: rgb(249, 250, 251);
  border: 1px solid rgb(209, 213, 219);
  border-radius: 6px;
  padding: 16px;
`;

const HeaderRow = styled(Row)`
  color: var(--secondary);
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  width: 100%;
  position: relative;
  margin-bottom: 16px;

  ${StyledIconBase} {
    height: 100%;
  }

  p {
    flex: 1 0 auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const GroupName = styled.div`
  width: 100%;

  p {
    line-height: 30px;
  }
`;

const NameInput = styled(Form.Control)`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: none;
  font-weight: inherit;
  padding: 0 4px;
  line-height: 1;
`;

const ActionButton = styled(IconButton)`
  width: 24px;
  height: 24px;
  box-shadow: none;
  background-color: transparent;
`;

const GroupOptions = styled(Col)`
  position: absolute;
  top: 100%;
  right: 2px;
  width: 190px;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  z-index: 1000;
  font-size: 14px;
  line-height: 24px;
`;

const TextBtn = styled.button<{ color?: string }>`
  border: none;
  box-shadow: none;
  background-color: transparent;
  display: flex;
  color: ${(props) => (props.color ? props.color : "#000")};
  align-items: center;
  padding: 10px 16px;
  gap: 5px;

  &:disabled {
    opacity: 0.5;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const DashboardRow = styled(Row)`
  gap: 24px;
  flex-wrap: wrap;
`;

const ItemWrap = styled.div`
  width: calc(20% - 12px);
`;

const CreateNewSlide = styled.button`
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 0, 0, 0.3);
  padding: 10px;
  height: 100px;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  color: rgba(0, 0, 0, 0.7);
  cursor: pointer;
  transition:
    color 0.1s ease-in-out,
    border-color 0.1s ease-in-out;
  background-color: transparent;

  svg {
    width: 30px;
    height: 30px;
    color: var(--widget-primary);
  }

  &:hover {
    color: var(--widget-colour);
    border-color: var(--widget-colour);
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }
`;
